import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Divider,
  Heading,
  Input,
  Select,
  ModalFooter,
  Alert,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DrawerFormField } from "pages/Borrowers/elements/AddBorrower";
import { AlertsContext } from "providers/alerts";
import { useContext, useEffect, useRef, useState } from "react";
import { apiRequest } from "utils/api";
import { faCircleExclamation } from "@fortawesome/pro-light-svg-icons";
import { formatFieldTypeValue } from "utils/helpers";
import PercentField from "components/elements/PercentField";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { getErrorMessage } from "utils/errorHandler";
import { useRoles } from "hooks/useRoles";

const EditBorrowerFields = ({
  borrower,
  loadBorrower,
  fields,
  setFields,
}: {
  borrower: any;
  loadBorrower: () => void;
  fields: any;
  setFields: any;
}) => {
  const { showSuccess, showError } = useContext(AlertsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useTenant();
  const btnRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [addNewField, setAddNewField] = useState(false);
  const [label_, setLabel] = useState("");
  const [fieldValue, setFieldValue] = useState("");
  const [selectedType, setType] = useState("");
  const [placeholder_, setPlaceholder] = useState("");
  const [borrowerFields, setBorrowerFields] = useState<any[]>([]);
  const [showEditField, setShowEditField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startDelete, setStartDelete] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [role, setRole] = useState<any>(
    borrower.role ? { label: borrower.role.name, value: borrower.role.id } : null
  );
  const { roles: borrowerRoles, isLoading: isLoadingRoles } = useRoles({
    company_id: borrower.company?.id,
    userType: "borrower",
  });
  const borrowerRoleOptions = (borrowerRoles || []).map((role: any) => ({
    label: role.name,
    value: role.id,
  }));

  async function saveChanges() {
    setSaving(true);
    try {
      await apiRequest("/supabase/borrower_detail/insert", {
        id: borrower.id,
        label: label_,
        type: selectedType,
        borrowerEditFieldsValues: fieldValue,
        placeholder: placeholder_,
        company: borrower.company,
      });
      showSuccess({
        title: "Saved Changes",
        description: "The borrower details have been created.",
      });
      loadBorrower();
      onClose();
    } catch (err) {
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setSaving(false);
      setLabel("");
      setType("");
      setPlaceholder("");
      setAddNewField(false);
      setShowEditField(false);
    }
  }

  const handleEditField = (fieldId: any) => {
    setShowEditField(prevState => {
      if (prevState === fieldId) {
        return false;
      } else {
        return fieldId;
      }
    });
  };

  async function saveFields() {
    try {
      setSaving(true);
      await apiRequest("/supabase/borrower_detail/update", {
        id: showEditField,
        borrowerId: borrower.id,
        label: label_,
        type: selectedType,
        placeholder: placeholder_,
        fieldValue: fieldValue,
      });

      showSuccess({
        title: "Saved Changes",
        description: "The borrower details have been updated.",
      });
      loadBorrower();
      onClose();
    } catch (err) {
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setSaving(false);
      setLabel("");
      setType("");
      setPlaceholder("");
      setShowEditField(false);
    }
  }

  const deleteLoanField = async () => {
    try {
      setLoading(true);

      await apiRequest("/supabase/borrower_detail/delete", {
        id: showEditField,
        borrowerId: borrower.id,
      });

      showSuccess({
        title: "Value Deleted",
        description: "The borrower details have been updated.",
      });
      loadBorrower();
      onClose();
    } catch (err) {
      showError({
        title: "Delete Failed",
        description: getErrorMessage(err),
      });
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getValueByFieldId = (fieldId: any) => {
    const field = fields.find((field: any) => field.id === fieldId);
    if (field) {
      const values = field.values;
      if (values && values.length > 0 && values[0].borrower === borrower.id) {
        return formatFieldTypeValue(field.type, values[0]?.value?.field);
      }
    }
    return "";
  };

  useEffect(() => {
    if (fields && fields.length > 0) {
      setBorrowerFields(fields);
    }
  }, []);

  const handleValueChange = (fieldId: any, event: any) => {
    const updatedFields = borrowerFields.map((field: any) => {
      if (field.id === fieldId) {
        field.values[0].value.field = event.target.value;
      }
      return field;
    });
    setBorrowerFields(updatedFields);
  };

  useEffect(() => {
    if (fields !== null) {
      const getFieldData = fields.filter((field: any) => {
        return field.id === showEditField;
      });
      setLabel(getFieldData[0]?.label);
      if (getFieldData[0]?.values && getFieldData[0]?.values[0]?.borrower === borrower.id) {
        setFieldValue(getFieldData[0]?.values[0]?.value?.field);
      }
      setType(getFieldData[0]?.type);
      setPlaceholder(getFieldData[0]?.placeholder);
    }
  }, [fields, showEditField]);

  const handleCancel = () => {
    setFieldValue("");
    setType("");
    setPlaceholder("");
    setLabel("");
    setShowEditField(false);
    setStartDelete(false);
    onClose();
  };

  return (
    <Box>
      <Button ref={btnRef} onClick={onOpen} variant="ghost" size="sm" color="primary">
        Edit
      </Button>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        onCloseComplete={handleCancel}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Edit Borrower</DrawerHeader>
          <DrawerBody>
            <Divider mx={"0"} my={"2"} />
            <Heading as="h5" size="sm" mb="3" mt="3">
              Additional Details
            </Heading>
            {fields &&
              fields.length > 0 &&
              fields
                .sort((a: any, b: any) => a.order - b.order)
                .map((field: any) => (
                  <FormControl
                    isRequired={field.required}
                    key={field.id}
                    w="100%"
                    alignItems="flex-start"
                    mb="22px"
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <FormLabel fontSize="14px" fontWeight="500" mb="0px">
                        {field.label}
                      </FormLabel>
                      <Button
                        size="sm"
                        color="#6200FF"
                        variant="link"
                        onClick={() => {
                          handleEditField(field.id);
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                    {(field.type === "text" || field.type === "encrypted") && (
                      <Input
                        value={getValueByFieldId(field.id)}
                        placeholder={field.placeholder}
                        variant="primary"
                        onChange={event => handleValueChange(field.id, event)}
                      />
                    )}
                    {field.type === "dropdown" && (
                      <Select flex="1">
                        <option value=""> {field.placeholder || "Select an option"} </option>
                        {field.options?.map((o: string) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </Select>
                    )}
                    {field.type === "date" && (
                      <Input
                        value={
                          getValueByFieldId(field.id)
                            ? moment(getValueByFieldId(field.id)).format("YYYY-MM-DD")
                            : ""
                        }
                        onChange={event => handleValueChange(field.id, event)}
                        placeholder="MM/DD/YYYY"
                        type="date"
                        variant="primary"
                        minHeight="2.5rem"
                      />
                    )}
                    {field.type === "percentage" && (
                      <PercentField
                        value={getValueByFieldId(field.id) || ""}
                        onChange={event => handleValueChange(field.id, event)}
                      />
                    )}
                    {field.type === "dollar" && (
                      <Input
                        value={getValueByFieldId(field.id)}
                        placeholder={field.placeholder}
                        variant="primary"
                        onChange={event => handleValueChange(field.id, event)}
                      />
                    )}
                    {field.type === "number" && (
                      <Input
                        value={getValueByFieldId(field.id)}
                        placeholder={field.placeholder}
                        variant="primary"
                        onChange={event => handleValueChange(field.id, event)}
                      />
                    )}

                    {/* Edit the fields */}
                    <Box>
                      {showEditField === field.id && (
                        <ModalFooter flexDirection="column">
                          <DrawerFormField
                            label={"Field Name"}
                            placeholder={"Field Name"}
                            value={label_}
                            onChange={setLabel}
                          />
                          {selectedType === "date" ? (
                            <>
                              <Text color={"black"} fontWeight={"600"} mr={"115px"}>
                                Field Value
                              </Text>
                              <Input
                                flex="1"
                                value={fieldValue}
                                onChange={event => setFieldValue(event.target.value)}
                                placeholder="MM/DD/YYYY"
                                type="date"
                                variant="primary"
                                minHeight="2.5rem"
                                mb={"10px"}
                              />
                            </>
                          ) : selectedType === "dropdown" ? (
                            <FormControl mb="12px">
                              <FormLabel
                                mb="7px"
                                fontWeight="600"
                                fontSize="17px"
                                lineHeight="28px"
                              >
                                Field Value
                              </FormLabel>
                              <Select
                                flex="1"
                                value={fieldValue || getValueByFieldId(field.id)}
                                onChange={e => setFieldValue(e.target.value)}
                              >
                                <option> {field.placeholder || "Select an option"} </option>
                                {field.options.map((o: string) => (
                                  <option key={o} value={o}>
                                    {o}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <DrawerFormField
                              label="Field Value"
                              placeholder={placeholder_}
                              value={fieldValue}
                              onChange={setFieldValue}
                            />
                          )}
                          <FormControl mb="16px" isRequired>
                            <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                              Field Type
                            </FormLabel>
                            <Select value={selectedType} onChange={e => setType(e.target.value)}>
                              <option value="date">Date</option>
                              <option value="text">Text</option>
                              <option value="percentage">Percentage</option>
                              <option value="dollar">Amount</option>
                              <option value="dropdown">Dropdown</option>
                              <option value="number">Number</option>
                            </Select>
                          </FormControl>
                          <DrawerFormField
                            label="Placeholder Text"
                            placeholder="Placeholder"
                            value={placeholder_}
                            onChange={setPlaceholder}
                          />
                          {fieldValue !== "" && (
                            <Box ml={"-63%"} mb={"10px"}>
                              <span>
                                <FontAwesomeIcon
                                  icon={["far", "trash"]}
                                  style={{
                                    color: "red",
                                  }}
                                />
                              </span>
                              <Button
                                background={"none"}
                                color={"red"}
                                border={"none"}
                                _hover={{
                                  background: "none",
                                }}
                                ml={"-10px"}
                                onClick={() => {
                                  setStartDelete(true);
                                  setShowButtons(true);
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          )}
                          {startDelete && (
                            <ModalFooter flexDirection="column" width={"140%"}>
                              <Alert
                                status="error"
                                borderRadius="8px"
                                border="1px"
                                borderColor="#FF144D"
                                variant="subtle"
                                flexDirection="column"
                                alignItems="left"
                                justifyContent="center"
                                textAlign="left"
                              >
                                <Box
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faCircleExclamation}
                                    style={{
                                      color: "#FF144D",
                                    }}
                                  />
                                  <h3
                                    style={{
                                      marginLeft: "8px",
                                    }}
                                  >
                                    <b>Delete field ?</b>
                                  </h3>
                                </Box>
                                <span
                                  style={{
                                    marginLeft: "25px",
                                  }}
                                >
                                  The value for this field will be deleted. Are you sure you want to
                                  delete?
                                </span>
                              </Alert>
                              <Box display="flex" flexDirection={"column"} w="100%" mt="12px">
                                <Button
                                  style={{
                                    backgroundColor: "#FF144D",
                                    color: "white",
                                  }}
                                  variant=""
                                  onClick={deleteLoanField}
                                  isLoading={loading}
                                >
                                  Yes, Delete value
                                </Button>
                                <Button
                                  style={{
                                    backgroundColor: "#ffffff",
                                    color: "#4A494B",
                                    borderColor: "#B6B3B9",
                                    borderWidth: "1px",
                                  }}
                                  variant="solid"
                                  onClick={() => {
                                    setShowButtons(false);
                                    setStartDelete(false);
                                  }}
                                  mt={"10px"}
                                >
                                  Do Not Delete
                                </Button>
                              </Box>
                            </ModalFooter>
                          )}
                          <Box display="flex" justifyContent="space-between">
                            <Button
                              variant="outline"
                              mr={3}
                              onClick={() => {
                                setAddNewField(false);
                                setShowEditField(false);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={saveFields}
                              variant={getCustomButtonVariant(user)}
                              flex="1"
                              isLoading={saving}
                              loadingText="Sending"
                            >
                              Save Field
                            </Button>
                          </Box>
                        </ModalFooter>
                      )}
                    </Box>
                  </FormControl>
                ))}
            <Button
              // ref={btnRef}
              position="relative"
              left="65px"
              onClick={() => setAddNewField(true)}
              variant="ghost"
              size="sm"
              color="primary"
            >
              + Add new field
            </Button>
            <Divider mx={"0"} my={"2"} />

            {/* For Adding New Fields: */}
            {addNewField && (
              <Box mt="5">
                <Heading as="h5" size="sm" mb="3">
                  New Field
                </Heading>
                <DrawerFormField
                  label="Label"
                  placeholder="Add Label"
                  value={label_}
                  onChange={setLabel}
                />
                <FormControl mb="16px" isRequired>
                  <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                    Field Type
                  </FormLabel>
                  <Select placeholder="Select..." onChange={e => setType(e.target.value)}>
                    <option value="date">Date</option>
                    <option value="text">Text</option>
                    <option value="percentage">Percentage</option>
                    <option value="dollar">Amount</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="number">Number</option>
                  </Select>
                </FormControl>

                {selectedType === "date" ? (
                  <>
                    <Text color={"black"} fontWeight={"600"} mr={"115px"}>
                      Field Value
                    </Text>
                    <Input
                      flex="1"
                      value={fieldValue}
                      onChange={event => setFieldValue(event.target.value)}
                      placeholder="MM/DD/YYYY"
                      type="date"
                      variant="primary"
                      minHeight="2.5rem"
                      mb={"10px"}
                    />
                  </>
                ) : (
                  <DrawerFormField
                    label="Field Value"
                    placeholder="Add Field Value..."
                    value={fieldValue}
                    onChange={setFieldValue}
                    cy="edit-propertyDetail-placeholder"
                  />
                )}
                <DrawerFormField
                  label="Placeholder Text"
                  placeholder="Placeholder"
                  value={placeholder_}
                  onChange={setPlaceholder}
                  cy="edit-borrower-placeholder"
                />
                {startDelete && (
                  <ModalFooter flexDirection="column">
                    <FormControl mb="16px" isRequired>
                      <FormLabel mb="7px" fontWeight="600" fontSize="17px" lineHeight="28px">
                        Field Type
                      </FormLabel>
                      <Select placeholder="Select..." onChange={e => setType(e.target.value)}>
                        <option value="datetime">Datetime</option>
                        <option value="text">Text</option>
                        <option value="percentage">Percentage</option>
                        <option value="dollar">Dollar</option>
                      </Select>
                    </FormControl>
                    <DrawerFormField
                      label="Placeholder Text"
                      placeholder="Placeholder"
                      value={placeholder_}
                      onChange={setPlaceholder}
                    />
                    <Box display="flex" justifyContent="space-between">
                      <Button variant="outline" mr={3} onClick={() => setAddNewField(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={saveChanges}
                        variant={getCustomButtonVariant(user)}
                        flex="1"
                        // isLoading={saving}
                        loadingText="Sending"
                      >
                        Save Field
                      </Button>
                    </Box>
                  </ModalFooter>
                )}
              </Box>
            )}
          </DrawerBody>
          <DrawerFooter>
            <Button variant="outline" mr={3} onClick={handleCancel}>
              Cancel
            </Button>
            {addNewField && (
              <Button
                onClick={saveChanges}
                variant={getCustomButtonVariant(user)}
                flex="1"
                isLoading={saving}
                loadingText="Sending"
              >
                Save Changes
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default EditBorrowerFields;
